<template>
  <div>
    <PageHeader title="Plan sales" />
    <SearchToolbar
      class="mb-6"
      :placeholder="$t('SearchPlanSales')"
      :available-filters="availableFilters"
      :selected-filters="selectedFilters"
      :initial-keywords="keywords"
      :has-active-period="dateRange.some(Boolean)"
      @update-filter-search="updateFilterSearch"
      @update-keyword-search="updateKeywordSearch"
      @clear-filters="clearPeriod"
    >
      <DateRangePicker placeholder="Période" v-model="dateRange" use-query-params />
    </SearchToolbar>
    <PlanSalesContent :current-page="currentPage" @updatePage="updatePage" />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import PlanSalesContent from "@/views/plan_sales/list/PlanSalesContent.vue";
import { DateRangePicker, PageHeader, SearchToolbar } from "@estaly/ui";

export default {
  components: {
    DateRangePicker,
    PageHeader,
    SearchToolbar,
    PlanSalesContent,
  },

  data: () => {
    return {
      availableFilters: {
        organizations: [],
        stores: [],
        chargedBy: [],
        status: [],
        billing: [],
      },
      selectedFilters: {
        organizations: [],
        stores: [],
        chargedBy: [],
        status: [],
        billing: [],
      },
      startDate: null,
      endDate: null,
      keywords: null,
      currentPage: 1,
    };
  },

  computed: {
    ...mapState("auth", ["signedIn"]),
    ...mapGetters("planSales", ["statuses", "chargedByValues"]),
    ...mapState("stores", { stores: "index" }),
    ...mapState("organizations", { organizations: "index" }),

    dateRange: {
      get() {
        return [this.startDate, this.endDate];
      },
      set(value) {
        if (value && value.length === 2) {
          this.handleNewDateSelected(value[0], value[1]);
        } else {
          this.handleNewDateSelected(null, null);
        }
      },
    },

    monthlyFilter() {
      const [billing] = this.selectedFilters.billing;

      if (this.selectedFilters.billing.length !== 1) return null;

      if (billing === "Monthly") return true;
      if (billing === "Annually") return false;

      return null;
    },

    planSaleParams() {
      return {
        startDate: this.dateRange[0],
        endDate: this.dateRange[1],
        chargedBy: this.selectedFilters.chargedBy,
        status: this.selectedFilters.status,
        monthly: this.monthlyFilter,
        organizationIds: this.selectedFilters.organizations,
        storeIds: this.selectedFilters.stores,
        keywords: this.keywords,
        showRenewals: true,
        includes: ["customer", "plan_sale_product", "store", "cancellation", "seller"],
      };
    },
  },

  async created() {
    this.loadQueryParams();

    this.availableFilters.status = this.statuses();
    this.availableFilters.billing = ["Monthly", "Annually"];
    this.availableFilters.chargedBy = this.chargedByValues();

    await this.withLoader(async () => {
      await Promise.all([
        this.fetchStores({ perPage: 2000, status: ["active", "inactive"] }),
        this.fetchOrganizations({ perPage: 2000 }),
      ]);

      this.availableFilters.stores = this.stores
        .filter((store) => store.salesChannel !== null)
        .map((store) => ({ label: store.salesChannel, value: store.id }));

      this.availableFilters.organizations = this.organizations.map((organization) => ({
        label: organization.name,
        value: organization.id,
      }));

      await this.fetchIndex({
        ...this.planSaleParams,
        page: this.currentPage,
      });
    });
  },

  methods: {
    ...mapActions("planSales", ["fetchIndex"]),
    ...mapActions("backoffice", ["withLoader"]),
    ...mapActions("stores", { fetchStores: "fetchIndex" }),
    ...mapActions("organizations", { fetchOrganizations: "fetchIndex" }),

    async retrievePlanSales() {
      await this.withLoader(async () => {
        await this.fetchIndex({
          ...this.planSaleParams,
          page: this.currentPage,
        });
      });
    },

    updatePage(page, refresh = true) {
      this.currentPage = page;
      if (refresh) this.retrievePlanSales();
    },

    updateFilterSearch(filterName, filterOptions) {
      this.selectedFilters[filterName] = filterOptions;
      this.updatePage(1);
    },

    updateKeywordSearch(keywords) {
      this.keywords = keywords;
      this.updateRouteQueryParams();
      this.updatePage(1);
    },

    handleNewDateSelected(startDate, endDate) {
      this.startDate = startDate;
      this.endDate = endDate;
      this.updatePage(1);
    },

    clearPeriod() {
      const query = { ...this.$route.query };
      delete query.start_date;
      delete query.end_date;
      this.$router.replace({ query });
      this.dateRange = [null, null];
    },

    loadQueryParams() {
      this.keywords = this.$route.query.keywords ?? null;
    },

    updateRouteQueryParams() {
      const query = { ...this.$route.query };
      if (this.keywords) query.keywords = this.keywords;
      else delete query.keywords;
      this.$router.replace({ query });
    },
  },
};
</script>
